/**
 * =============================================================================
 * MY QUESTIONS COMPONENT
 * =============================================================================
 *
 * Displays a list of questions submitted by the current guest user.
 * Uses the guest_token cookie (sent automatically with requests) to identify the user.
 *
 * ARCHITECTURE REFERENCE: PHASE_0B_SPEC.md §2.5.5
 * PATTERN REFERENCE: PHASE_0B_PATTERNS.md §7
 *
 * States:
 * - Loading: Shows a spinner while fetching
 * - Empty: "You haven't submitted any questions yet"
 * - Populated: List of questions with title, status, and date
 * - Error: Error message with retry option
 *
 * =============================================================================
 */

import React from 'react';
import { resolveQuestionStatusPresentation } from '@find-my-part/types';
import { useMyQuestions, useStyleInjection, useOnlineStatus } from '../hooks';
import { QuestionSkeleton } from './QuestionSkeleton';
import { OfflineNotice } from './OfflineNotice';
import { logger } from '../lib/logger';
import { resolveMyQuestionStage, type MyQuestionStageAction } from '../lib/myQuestionStage';
import { useViewerState } from '../viewer';
import { BREAKPOINTS } from '../tokens';

// =============================================================================
// TYPES
// =============================================================================

export interface MyQuestionsProps {
  /** Additional CSS class name */
  className?: string;
  /** Callback when a question is clicked */
  onQuestionClick?: (questionId: string) => void;
  /** Navigate to solved-case discovery for a duplicate question. */
  onBrowseQuestions?: () => void;
  /** Start a new photo-first question from the confirmed-empty state. */
  onAskQuestion?: () => void;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Format a date for display.
 * Returns a human-readable date string.
 *
 * Uses the browser's default locale via `toLocaleDateString(undefined, ...)`.
 * This ensures dates are displayed according to the user's local preferences,
 * which is appropriate for a customer-facing widget embedded in various storefronts.
 *
 * @param timestamp - Unix timestamp in milliseconds, ISO date string, or null/undefined
 * @returns Formatted date string, or 'Unknown date' if invalid/missing
 */
function formatDate(timestamp: number | string | null | undefined): string {
  // Handle null/undefined explicitly
  if (timestamp == null) {
    return 'Unknown date';
  }

  const date = new Date(timestamp);

  // Check for invalid date
  if (isNaN(date.getTime())) {
    return 'Unknown date';
  }

  // Use toLocaleDateString for consistent formatting
  // Note: undefined locale uses browser default, respecting user preferences
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get a safe ISO date string for the datetime attribute.
 * Returns undefined for invalid dates, which omits the datetime attribute.
 *
 * @param timestamp - Unix timestamp in milliseconds, ISO date string, or null/undefined
 * @returns ISO date string or undefined if invalid/missing
 */
function getDateTimeAttr(timestamp: number | string | null | undefined): string | undefined {
  // Handle null/undefined explicitly
  if (timestamp == null) {
    return undefined;
  }

  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return date.toISOString();
}

// =============================================================================
// ERROR SANITIZATION (P1-16)
// =============================================================================

/**
 * Map of known error codes/patterns to user-friendly messages.
 * These messages should be helpful without exposing internal details.
 */
const USER_FRIENDLY_ERRORS: Record<string, string> = {
  // Network-related errors
  NETWORK_ERROR: 'Connection failed. Please check your internet and try again.',
  OFFLINE: "You're currently offline. Please check your connection and try again.",
  REQUEST_TIMEOUT: 'The request took too long. Please try again.',
  MAX_RETRIES_EXCEEDED: 'Unable to reach the server. Please try again later.',
  PROTOCOL_ERROR: 'Unable to load your questions. Please try again later.',

  // Widget initialization errors
  WIDGET_INIT_ERROR: 'Unable to load your questions. Please refresh the page.',

  // Auth/context errors - should not expose header names
  UNAUTHORIZED: 'Please refresh the page and try again.',
  VALIDATION_ERROR: 'Unable to load your questions. Please try again later.',
  NOT_FOUND: 'Unable to load your questions. Please try again later.',
  STORE_NOT_FOUND: 'Unable to load your questions. Please try again later.',
};

const ONLINE_RECOVERY_MESSAGE = "You're back online. Try again to load your questions.";

/**
 * Sanitize error messages to prevent information disclosure.
 * Maps known error codes to user-friendly messages and logs the
 * original error for debugging.
 *
 * Security: Prevents exposure of internal details like header names,
 * API endpoints, or technical error descriptions to end users.
 *
 * @param message - The raw error message from the API
 * @param code - Optional error code from the API response
 * @returns User-friendly error message safe for display
 */
function sanitizeErrorMessage(message: string, code?: string): string {
  // First, check if we have a matching error code
  if (code && Object.prototype.hasOwnProperty.call(USER_FRIENDLY_ERRORS, code)) {
    const friendlyMessage = USER_FRIENDLY_ERRORS[code];
    // Log original message for debugging (development only via logger.debug)
    logger.debug('SANITIZED_ERROR', { code, originalMessage: message });
    return friendlyMessage;
  }

  // Check if the message contains any known error patterns
  // This catches cases where the error code might not be set
  const lowerMessage = message.toLowerCase();

  if (lowerMessage.includes('x-store-hash') || lowerMessage.includes('store hash')) {
    // Don't expose header names to users
    logger.error('STORE_HASH_ERROR', null, { originalMessage: message });
    return 'Unable to load your questions. Please refresh the page.';
  }

  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('authentication')) {
    logger.error('AUTH_ERROR', null, { originalMessage: message });
    return 'Please refresh the page and try again.';
  }

  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    logger.error('NETWORK_ERROR', null, { originalMessage: message });
    return 'Connection failed. Please check your internet and try again.';
  }

  if (lowerMessage.includes('timeout')) {
    logger.error('TIMEOUT_ERROR', null, { originalMessage: message });
    return 'The request took too long. Please try again.';
  }

  // For any other unrecognized error, log it and return generic message
  logger.error('UNKNOWN_ERROR', null, { originalMessage: message });
  return 'Something went wrong. Please try again later.';
}

// =============================================================================
// STATUS FORMATTING
// =============================================================================

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Component to display the user's submitted questions.
 *
 * This component fetches questions on mount using the guest_token cookie.
 * The cookie is automatically included with requests via `credentials: 'include'`.
 *
 * The server explicitly distinguishes a confirmed empty history from a browser
 * where guest history cannot be recovered.
 *
 * @example
 * ```tsx
 * <MyQuestions
 *   onQuestionClick={(id) => navigate(`/question/${id}`)}
 *   className="my-container"
 * />
 * ```
 */
export function MyQuestions({
  className = '',
  onQuestionClick,
  onBrowseQuestions,
  onAskQuestion,
}: MyQuestionsProps): React.ReactElement {
  // Inject styles once per unique ID, shared across all MyQuestions instances
  useStyleInjection(myQuestionsStyles, 'fmp-my-questions-styles');

  // Track online/offline status for error handling and retry logic
  const isOnline = useOnlineStatus();
  const { loadState, retry } = useMyQuestions(isOnline);
  const viewerState = useViewerState();
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  const handleRetry = (): void => {
    // Move focus before the retry button leaves the DOM. The stable page heading
    // remains the transition anchor; terminal state is announced without
    // reclaiming focus if the customer moves elsewhere while the request runs.
    headingRef.current?.focus();
    retry();
  };

  const recoveredFromOffline =
    loadState.status === 'error' && isOnline && loadState.code === 'OFFLINE';
  const statusMessage =
    loadState.status === 'success'
      ? loadState.questions.length === 0
        ? 'No questions yet.'
        : `${loadState.questions.length} question${loadState.questions.length === 1 ? '' : 's'} loaded.`
      : loadState.status === 'unavailable'
        ? "We can't recall your previous questions in this browser."
        : recoveredFromOffline
          ? "You're back online. Try again to load your questions."
          : '';
  const renderStatusAnnouncer = (): React.ReactElement => (
    <div
      className="fmp-visually-hidden"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      data-testid="my-questions-status"
    >
      {statusMessage}
    </div>
  );

  const renderPageHeader = (includeDescription: boolean): React.ReactElement => (
    <header className="fmp-questions__header">
      <h2
        ref={headingRef}
        className="fmp-questions__title fmp-questions__focus-target"
        tabIndex={-1}
      >
        My Questions
      </h2>
      {includeDescription && (
        <p className="fmp-questions__description">
          Track the status of your submitted questions below.
        </p>
      )}
    </header>
  );

  const renderAction = (
    action: MyQuestionStageAction,
    questionId: string,
    questionTitle: string
  ): React.ReactElement | null => {
    if (action.kind === 'detail' && onQuestionClick) {
      return (
        <button
          type="button"
          className="fmp-question-item__action"
          aria-label={`${action.label}: ${questionTitle}`}
          onClick={() => onQuestionClick(questionId)}
        >
          {action.label}
        </button>
      );
    }

    if (action.kind === 'browse' && onBrowseQuestions) {
      return (
        <button
          type="button"
          className="fmp-question-item__action"
          aria-label={`${action.label}: ${questionTitle}`}
          onClick={onBrowseQuestions}
        >
          {action.label}
        </button>
      );
    }

    return null;
  };

  // Render loading state with skeleton placeholders
  if (loadState.status === 'loading') {
    return (
      <div className={`fmp-questions ${className}`} data-viewer-state={viewerState}>
        {renderStatusAnnouncer()}
        {renderPageHeader(true)}
        <QuestionSkeleton count={3} />
      </div>
    );
  }

  // Render error state
  if (loadState.status === 'error') {
    const errorHeading = (
      <h3 className="fmp-questions__error-title">We couldn&apos;t load your questions</h3>
    );

    return (
      <div className={`fmp-questions ${className}`} data-viewer-state={viewerState}>
        {renderStatusAnnouncer()}
        {renderPageHeader(false)}
        <div className="fmp-questions__error">
          {!isOnline ? (
            <>
              {errorHeading}
              <OfflineNotice />
            </>
          ) : recoveredFromOffline ? (
            <div className="fmp-questions__state-content">
              {errorHeading}
              <p className="fmp-questions__recovery-message">{ONLINE_RECOVERY_MESSAGE}</p>
            </div>
          ) : (
            <div className="fmp-questions__state-content" role="alert">
              {errorHeading}
              <p className="fmp-questions__error-message">
                {sanitizeErrorMessage(loadState.message, loadState.code)}
              </p>
            </div>
          )}
          <button
            type="button"
            onClick={handleRetry}
            className="fmp-btn fmp-btn--secondary"
            aria-label="Retry loading questions"
            disabled={!isOnline}
          >
            {isOnline ? 'Try Again' : 'Waiting for connection...'}
          </button>
        </div>
      </div>
    );
  }

  // Render unavailable-history state (ADR-024 item (c)): a valid guest identity was not
  // present (cookie missing / blocked by ITP-ETP / expired / cleared), so we cannot recall
  // this browser's history. Calm + informational: the persistent, initially-empty
  // My Questions status node announces it politely, while visible state content
  // remains outside an alert. No retry — a repeated fetch cannot restore a blocked
  // cookie — and no /manage link: the widget holds no capability token, and the
  // working management link is delivered by email.
  if (loadState.status === 'unavailable') {
    return (
      <div className={`fmp-questions ${className}`} data-viewer-state={viewerState}>
        {renderStatusAnnouncer()}
        {renderPageHeader(false)}
        <div className="fmp-questions__empty">
          <div className="fmp-questions__state-content">
            <h3 className="fmp-questions__empty-title">
              We can&apos;t recall your previous questions in this browser.
            </h3>
            <p className="fmp-questions__empty-message">
              {/* Copy is deliberately CONDITIONAL: the confirmation email is a per-tenant
                  opt-in and its send is gated (email config, global unsubscribe, queue vs.
                  delivery), pre-2026-07-05 submissions carry no manage link, and status
                  updates arrive as separate emails — so none of these can be asserted
                  unconditionally. The quoted link text is kept byte-identical to the
                  confirmation-email template's "Manage your photos" button
                  (services/email/templates.ts). */}
              Check your email for updates about your questions. If you received a submission
              confirmation email, it may include a &ldquo;Manage your photos&rdquo; link for that
              submission. If you need help, contact store support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (loadState.questions.length === 0) {
    return (
      <div className={`fmp-questions ${className}`} data-viewer-state={viewerState}>
        {renderStatusAnnouncer()}
        {renderPageHeader(false)}
        <div className="fmp-questions__empty">
          <svg
            className="fmp-questions__empty-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div className="fmp-questions__state-content">
            <h3 className="fmp-questions__empty-title">No Questions Yet</h3>
            <p className="fmp-questions__empty-message">
              You haven&apos;t submitted any questions yet.
            </p>
          </div>
          {onAskQuestion && (
            <button
              type="button"
              className="fmp-btn fmp-btn--primary fmp-questions__empty-action"
              onClick={onAskQuestion}
            >
              Ask with photos
            </button>
          )}
        </div>
      </div>
    );
  }

  // Render populated state
  return (
    <div className={`fmp-questions ${className}`} data-viewer-state={viewerState}>
      {renderStatusAnnouncer()}
      {renderPageHeader(true)}

      <ul className="fmp-questions__list" role="list">
        {loadState.questions.map((question) => {
          const presentation = resolveQuestionStatusPresentation(question.status);
          const stage = resolveMyQuestionStage(question.status);
          const action = renderAction(stage.action, question.questionId, question.title);

          return (
            <li
              key={question.questionId}
              className="fmp-question-item"
              data-my-question-stage={stage.code}
            >
              <div className="fmp-question-item__header">
                <h3 className="fmp-question-item__title">{question.title}</h3>
                <span className={`fmp-status-badge fmp-status-badge--${stage.tone}`}>
                  <span className="fmp-visually-hidden">Status: </span>
                  {presentation.customerLabel}
                </span>
              </div>
              <p className="fmp-question-item__stage">{stage.copy}</p>
              <div className="fmp-question-item__footer">
                <div className="fmp-question-item__meta">
                  <span>
                    Submitted{' '}
                    <time dateTime={getDateTimeAttr(question.createdAt)}>
                      {formatDate(question.createdAt)}
                    </time>
                  </span>
                  {question.imageCount > 0 && (
                    <span className="fmp-question-item__images">
                      <svg
                        className="fmp-question-item__image-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                      </svg>
                      {question.imageCount}
                      <span className="fmp-visually-hidden">
                        {' '}
                        image{question.imageCount !== 1 ? 's' : ''}
                      </span>
                    </span>
                  )}
                </div>
                {action}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const myQuestionsStyles = `
  :where(.fmp-widget-scope) .fmp-questions {
    padding-block: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-questions__title {
    margin: 0 0 var(--fmp-space-sm) 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-xl);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-questions__focus-target:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
  }

  :where(.fmp-widget-scope) .fmp-questions__description {
    margin: 0 0 var(--fmp-space-lg) 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-questions__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--fmp-size-60) var(--fmp-space-xl);
    text-align: center;
    color: var(--fmp-color-text-muted);
    font-size: var(--fmp-font-size-base);
    gap: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-questions__spinner {
    width: var(--fmp-size-32);
    height: var(--fmp-size-32);
    border: calc(var(--fmp-border-width) + var(--fmp-border-width-strong)) solid var(--fmp-color-progress-track);
    border-top-color: var(--fmp-color-primary);
    border-radius: 50%;
    animation: fmp-spin var(--fmp-duration-spin) linear infinite;
  }

  @keyframes fmp-spin {
    to {
      transform: rotate(360deg);
    }
  }

  :where(.fmp-widget-scope) .fmp-questions__error {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--fmp-size-40) var(--fmp-space-xl);
    text-align: center;
    gap: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-questions__error-title {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-questions__error-message {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-error);
  }

  :where(.fmp-widget-scope) .fmp-questions__recovery-message {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text);
  }

  :where(.fmp-widget-scope) .fmp-questions__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: var(--fmp-size-40) var(--fmp-space-lg);
    text-align: center;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-questions__state-content {
    display: grid;
    justify-items: center;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-questions__empty-icon {
    width: var(--fmp-size-48);
    height: var(--fmp-size-48);
    color: var(--fmp-color-text-muted); /* WCAG AA compliant (5.04:1 contrast) */
  }

  :where(.fmp-widget-scope) .fmp-questions__empty-title {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-questions__empty-message {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-questions__empty-action {
    width: 100%;
    margin-top: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-questions__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-question-item {
    padding: var(--fmp-size-14);
    background-color: var(--fmp-color-surface);
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-surface);
  }

  :where(.fmp-widget-scope) .fmp-question-item__header {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--fmp-space-sm);
    margin-bottom: var(--fmp-space-sm);
  }

  :where(.fmp-widget-scope) .fmp-question-item__title {
    margin: 0;
    max-inline-size: var(--fmp-layout-prose-max);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
    line-height: var(--fmp-line-height-body);
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }

  :where(.fmp-widget-scope) .fmp-question-item__stage {
    margin: 0 0 var(--fmp-space-md);
    max-inline-size: var(--fmp-layout-prose-max);
    color: var(--fmp-color-text-secondary);
    font-size: var(--fmp-font-size-sm);
    line-height: var(--fmp-line-height-body);
    overflow-wrap: anywhere;
  }

  :where(.fmp-widget-scope) .fmp-skeleton__stage {
    width: var(--fmp-skeleton-title-inline);
    margin: 0 0 var(--fmp-space-md);
  }

  :where(.fmp-widget-scope) .fmp-question-item__footer {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    gap: var(--fmp-space-lg);
  }

  :where(.fmp-widget-scope) .fmp-question-item__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--fmp-space-lg);
    font-size: var(--fmp-font-size-xs);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-question-item__images {
    display: inline-flex;
    align-items: center;
    gap: var(--fmp-space-xs);
  }

  :where(.fmp-widget-scope) .fmp-question-item__image-icon {
    width: var(--fmp-size-14);
    height: var(--fmp-size-14);
  }

  :where(.fmp-widget-scope) .fmp-question-item__action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: var(--fmp-size-44);
    min-height: var(--fmp-size-44);
    width: 100%;
    flex: 0 0 auto;
    padding: var(--fmp-space-sm) var(--fmp-space-lg);
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-surface);
    color: var(--fmp-color-primary);
    font: inherit;
    font-weight: var(--fmp-font-weight-semibold);
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard), box-shadow var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-question-item__action:hover {
    background-color: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-question-item__action:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-status-badge {
    display: inline-block;
    padding: var(--fmp-space-xs) var(--fmp-size-10);
    border-radius: var(--fmp-radius-pill);
    font-size: var(--fmp-font-size-xs);
    font-weight: var(--fmp-font-weight-semibold);
    white-space: nowrap;
  }

  :where(.fmp-widget-scope) .fmp-status-badge--neutral {
    background-color: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-muted);
  }

  :where(.fmp-widget-scope) .fmp-status-badge--info {
    background-color: var(--fmp-color-info-bg);
    color: var(--fmp-color-info);
  }

  :where(.fmp-widget-scope) .fmp-status-badge--success {
    background-color: var(--fmp-color-success-bg);
    color: var(--fmp-color-success);
  }

  :where(.fmp-widget-scope) .fmp-status-badge--warning {
    background-color: var(--fmp-color-warning-bg);
    color: var(--fmp-color-warning-text);
  }

  :where(.fmp-widget-scope) .fmp-status-badge--danger {
    background-color: var(--fmp-color-danger-bg);
    color: var(--fmp-color-danger);
  }

  :where(.fmp-widget-scope) .fmp-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--fmp-space-sm);
    min-height: var(--fmp-size-44);
    padding: var(--fmp-space-md) var(--fmp-space-2xl);
    border: none;
    border-radius: var(--fmp-radius-control);
    font-size: var(--fmp-font-size-base);
    font-weight: var(--fmp-font-weight-semibold);
    font-family: inherit;
    cursor: pointer;
    transition: background-color var(--fmp-duration-normal) var(--fmp-ease-standard), opacity var(--fmp-duration-normal) var(--fmp-ease-standard);
  }

  :where(.fmp-widget-scope) .fmp-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  :where(.fmp-widget-scope) .fmp-btn--secondary {
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    background-color: var(--fmp-color-surface-muted);
    color: var(--fmp-color-text-secondary);
  }

  :where(.fmp-widget-scope) .fmp-questions .fmp-btn--primary {
    border: var(--fmp-border-width) solid var(--fmp-color-primary);
    background-color: var(--fmp-color-primary);
    color: var(--fmp-color-text-on-primary);
  }

  :where(.fmp-widget-scope) .fmp-questions .fmp-btn--primary:hover:not(:disabled) {
    border-color: var(--fmp-color-primary-hover);
    background-color: var(--fmp-color-primary-hover);
  }

  :where(.fmp-widget-scope) .fmp-questions .fmp-btn:focus-visible {
    outline: var(--fmp-outline-width) var(--fmp-outline-style) var(--fmp-color-focus);
    outline-offset: var(--fmp-outline-offset);
    box-shadow: var(--fmp-shadow-focus);
  }

  :where(.fmp-widget-scope) .fmp-btn--secondary:hover:not(:disabled) {
    background-color: var(--fmp-color-surface-hover);
  }

  /* Mobile-first fallback; enhance from the widget's actual host-container width. */
  @container fmp-widget (min-width: ${BREAKPOINTS.container.compact}) {
    :where(.fmp-widget-scope) .fmp-questions {
      padding-block: var(--fmp-space-2xl);
    }

    :where(.fmp-widget-scope) .fmp-questions__description {
      margin-bottom: var(--fmp-space-2xl);
    }

    :where(.fmp-widget-scope) .fmp-question-item {
      padding: var(--fmp-space-lg);
    }

    :where(.fmp-widget-scope) .fmp-question-item__header {
      flex-direction: row;
      align-items: flex-start;
      gap: var(--fmp-space-md);
    }

    :where(.fmp-widget-scope) .fmp-question-item__title {
      font-size: var(--fmp-font-size-base);
    }

    :where(.fmp-widget-scope) .fmp-question-item__footer {
      flex-direction: row;
      align-items: center;
    }

    :where(.fmp-widget-scope) .fmp-question-item__action {
      width: auto;
    }

    :where(.fmp-widget-scope) .fmp-questions__empty-action {
      width: auto;
    }

    :where(.fmp-widget-scope) .fmp-question-item__meta {
      font-size: var(--fmp-font-size-sm);
    }
  }

  /* High contrast mode support for accessibility */
  @media (prefers-contrast: high) {
    :where(.fmp-widget-scope) .fmp-question-item__action:focus-visible {
      outline-color: currentColor;
      box-shadow: none;
      border-width: var(--fmp-border-width-strong);
    }

    :where(.fmp-widget-scope) .fmp-status-badge {
      border: var(--fmp-border-width) solid currentColor;
    }

    :where(.fmp-widget-scope) .fmp-btn:focus-visible {
      outline: var(--fmp-outline-width) var(--fmp-outline-style) currentColor;
      outline-offset: var(--fmp-outline-offset);
    }
  }

  /* P1-26 Review Fix: Reduced motion support for accessibility consistency */
  @media (prefers-reduced-motion: reduce) {
    :where(.fmp-widget-scope) .fmp-questions__spinner {
      animation: none;
    }

    :where(.fmp-widget-scope) .fmp-question-item,
    :where(.fmp-widget-scope) .fmp-question-item__action,
    :where(.fmp-widget-scope) .fmp-btn {
      transition: none;
    }
  }
`;
